import { useState } from 'react';
import type { ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    CloverIpgSystemId,
    CloverIpgSystemIdKey,
    defaultDemoIpgConfigByKey,
    defaultIpgConfigByKey,
    CloverGatewayEnvironment,
    type CloverIpgConfig,
    type CloverIpgConfigCustomization,
    CloverIpgConfigCustomizationSchema,
    type MerchantConfig,
    type TerminalDeviceListItem,
} from '@anchorbase/pillar-sdk';
import {
    CloverReadinessCheckStatus,
    type CloverReadinessCheck,
    type CloverReadinessResult,
} from '@anchorbase/pillar-sdk/ipg/clover/readiness';
import {
    CloverOAuthAuthorizationState,
    type CloverOAuthAuthorizationStatus,
} from '@anchorbase/pillar-sdk/ipg/clover/oauth';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { OmnisearchSelect } from '@/components/ui/omnisearch-select';
import {
    ApiError,
    paymentsInfrastructureApi,
    sambaConfigApi,
} from '@/lib/pillarApi';
import {
    formatIpgPaymentFeatureCapabilityStateLabel,
    IpgPaymentFeatureCapabilityState,
    type IpgConfigEditorAdapter,
    type IpgPaymentFeatureCapabilityDisplayRule,
} from '../../ipg-config-editor-adapter';

export interface CloverOrganizationIpgConfigEditorState {
    readonly ipgSystemId: typeof CloverIpgSystemId;
    readonly ipgSystemIdKey: typeof CloverIpgSystemIdKey;
    readonly environment: CloverGatewayEnvironment;
    readonly portalUrl: string;
    readonly urlPattern: CloverIpgConfig['urlPattern'];
    readonly allowedWebsites: CloverIpgConfig['allowedWebsites'];
    readonly capabilityDisplayRules:
        readonly IpgPaymentFeatureCapabilityDisplayRule[];
}

export interface CloverOrganizationIpgConfigEditorProps {
    readonly organizationUuid: string;
    readonly editorState: CloverOrganizationIpgConfigEditorState;
    readonly onChange: (
        nextState: CloverOrganizationIpgConfigEditorState
    ) => void;
    readonly onPresetConfigChange: (
        ipgConfig: CloverIpgConfigCustomization
    ) => void;
    readonly disabled: boolean;
}

export interface CloverOrganizationIpgReadOnlySummaryProps {
    readonly organizationUuid: string;
    readonly editorState: CloverOrganizationIpgConfigEditorState;
}

export interface CloverOrganizationIpgReadinessSummaryProps {
    readonly organizationUuid: string;
    readonly portalUrl: string;
}

const CLOVER_READINESS_TERMINAL_PAGE_SIZE = 50;

function formatOAuthExpiration(expiresTimestamp: string | null): string {
    if (expiresTimestamp === null) {
        return 'Not reported by Clover';
    }
    const expiresAt = new Date(expiresTimestamp);
    const remainingMilliseconds = expiresAt.getTime() - Date.now();
    const absolute = expiresAt.toLocaleString();
    if (remainingMilliseconds <= 0) {
        return `${absolute} (expired)`;
    }
    const remainingHours = Math.ceil(remainingMilliseconds / (60 * 60 * 1000));
    const eta =
        remainingHours < 48
            ? `${remainingHours} hour${remainingHours === 1 ? '' : 's'}`
            : `${Math.ceil(remainingHours / 24)} days`;
    return `${absolute} (in ${eta})`;
}

const CloverReadinessCheckDefinitions = [
    { key: 'credential', label: 'Credentials' },
    { key: 'ecommerceKey', label: 'Ecommerce' },
    { key: 'cloudPayDevice', label: 'Cloud Pay' },
    { key: 'multiPay', label: 'Reusable tokens' },
] as const satisfies ReadonlyArray<{
    readonly key: keyof CloverReadinessResult;
    readonly label: string;
}>;

const CloverDynamicReadinessFeatureIds: ReadonlySet<string> = new Set([
    'ecommerce_tokenization',
    'rest_pay_display',
    'terminal_payments',
    'terminal_tokenization',
    'heartbeat',
]);

export const CloverOrganizationIpgCapabilityDisplayRules: readonly IpgPaymentFeatureCapabilityDisplayRule[] =
    [
        {
            featureId: 'ecommerce_tokenization',
            label: 'Ecommerce tokenization',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip:
                'Clover Ecommerce tokenization can create payment tokens without storing PAN in the app.',
        },
        {
            featureId: 'rest_pay_display',
            label: 'REST Pay Display',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip:
                'Clover REST Pay Display can process terminal payments through supported devices.',
        },
        {
            featureId: 'terminal_payments',
            label: 'Terminal payments',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip: 'Clover terminal payment APIs are configured.',
        },
        {
            featureId: 'terminal_tokenization',
            label: 'Terminal tokenization',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip:
                'Clover can tokenize cards from a terminal for stored-card payments.',
        },
        {
            featureId: 'heartbeat',
            label: 'Heartbeat',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip: 'Clover merchant lookup can verify gateway readiness.',
        },
    ];

function cloneCloverOrganizationIpgConfig(
    config: CloverIpgConfig
): CloverIpgConfig {
    return {
        ...config,
        allowedWebsites: config.allowedWebsites.map((allowedWebsite) => ({
            ...allowedWebsite,
            allowedUrlPatterns: [...allowedWebsite.allowedUrlPatterns],
            urlPatternsRules: allowedWebsite.urlPatternsRules.map(
                (fetchPolicy) => ({
                    action: fetchPolicy.action,
                    urlPatterns: [...fetchPolicy.urlPatterns],
                })
            ),
            iframeUrlPatterns: allowedWebsite.iframeUrlPatterns.map(
                (iframePolicy) => ({
                    action: iframePolicy.action,
                    urlPatterns: [...iframePolicy.urlPatterns],
                })
            ),
        })),
        rpaModules: [...config.rpaModules],
    };
}

function getCloverOrganizationIpgPortalUrl(config: CloverIpgConfig): string {
    return config.allowedWebsites[0]?.url ?? '';
}

function getProductionCloverOrganizationIpgConfig(): CloverIpgConfig {
    const config = defaultIpgConfigByKey[CloverIpgSystemIdKey];
    if (config.ipg_id !== CloverIpgSystemId) {
        throw new Error('Invalid Clover production IPG configuration.');
    }
    return config;
}

function getDemoCloverOrganizationIpgConfig(): CloverIpgConfig {
    const demoIpgConfig = defaultDemoIpgConfigByKey[CloverIpgSystemIdKey];
    if (
        demoIpgConfig === undefined ||
        demoIpgConfig.ipg_id !== CloverIpgSystemId
    ) {
        throw new Error('Missing Clover demo IPG configuration.');
    }
    return demoIpgConfig;
}

function getEffectiveCloverOrganizationIpgConfig(
    merchantConfig: MerchantConfig | undefined
): CloverIpgConfig {
    const persistedCloverConfig = merchantConfig?.ipg[CloverIpgSystemIdKey];
    if (persistedCloverConfig?.ipg_id === CloverIpgSystemId) {
        return cloneCloverOrganizationIpgConfig(persistedCloverConfig);
    }
    return cloneCloverOrganizationIpgConfig(
        getProductionCloverOrganizationIpgConfig()
    );
}

export function buildCloverOrganizationIpgConfigEditorState(
    merchantConfig: MerchantConfig | undefined
): CloverOrganizationIpgConfigEditorState {
    const effectiveConfig =
        getEffectiveCloverOrganizationIpgConfig(merchantConfig);
    return {
        ipgSystemId: CloverIpgSystemId,
        ipgSystemIdKey: CloverIpgSystemIdKey,
        environment: effectiveConfig.environment,
        portalUrl: getCloverOrganizationIpgPortalUrl(effectiveConfig),
        urlPattern: effectiveConfig.urlPattern,
        allowedWebsites: effectiveConfig.allowedWebsites,
        capabilityDisplayRules:
            CloverOrganizationIpgCapabilityDisplayRules,
    };
}

function formatCloverOrganizationIpgEnvironmentLabel(
    environment: CloverGatewayEnvironment
): string {
    if (environment === CloverGatewayEnvironment.PRODUCTION) {
        return 'Production';
    }
    return 'Sandbox';
}

export function buildCloverOrganizationIpgConfigForSaveOrThrow(
    editorState: CloverOrganizationIpgConfigEditorState
): CloverIpgConfigCustomization {
    return CloverIpgConfigCustomizationSchema.parse({
        ipg_id: CloverIpgSystemId,
        environment: editorState.environment,
    });
}

function CloverOrganizationIpgEnvironmentRadio(props: {
    readonly label: string;
    readonly checked: boolean;
    readonly disabled: boolean;
    readonly tooltip: string;
    readonly onChange: () => void;
}): ReactElement {
    return (
        <div className="flex items-center gap-2 text-sm">
            <span
                className="-m-[3px] inline-flex p-[3px]"
                title={props.tooltip}
                onClick={(event) => {
                    if (
                        props.disabled ||
                        event.target !== event.currentTarget
                    ) {
                        return;
                    }
                    props.onChange();
                }}
            >
                <input
                    type="radio"
                    aria-label={props.label}
                    name="clover_ipg_environment"
                    checked={props.checked}
                    disabled={props.disabled}
                    title={props.tooltip}
                    className="h-4 w-4 accent-blue-600 disabled:opacity-50"
                    onChange={() => {
                        if (!props.disabled) {
                            props.onChange();
                        }
                    }}
                />
            </span>
            <span>{props.label}</span>
        </div>
    );
}

function buildCloverOrganizationIpgPresetState(
    editorState: CloverOrganizationIpgConfigEditorState,
    environment: CloverGatewayEnvironment
): CloverOrganizationIpgConfigEditorState {
    const presetConfig =
        environment === CloverGatewayEnvironment.SANDBOX
            ? getDemoCloverOrganizationIpgConfig()
            : getProductionCloverOrganizationIpgConfig();
    return {
        ...editorState,
        environment,
        portalUrl: getCloverOrganizationIpgPortalUrl(presetConfig),
        urlPattern: presetConfig.urlPattern,
        allowedWebsites:
            cloneCloverOrganizationIpgConfig(presetConfig).allowedWebsites,
    };
}

function getCloverEnvironmentRadioTooltip(input: {
    readonly environment: CloverGatewayEnvironment;
    readonly isPending: boolean;
    readonly isError: boolean;
    readonly isAvailable: boolean;
    readonly editorDisabled: boolean;
}): string {
    if (input.editorDisabled) {
        return 'Gateway configuration editing is currently unavailable.';
    }
    if (input.isPending) {
        return 'Checking Clover environment availability.';
    }
    if (input.isError) {
        return 'Clover environment availability could not be loaded.';
    }
    if (!input.isAvailable) {
        return `The ${formatCloverOrganizationIpgEnvironmentLabel(input.environment)} Clover app is not configured for this Anchorbase Portal environment.`;
    }
    return `Use the ${formatCloverOrganizationIpgEnvironmentLabel(input.environment)} Clover app.`;
}

export function CloverOrganizationIpgConfigEditor({
    organizationUuid,
    editorState,
    onChange,
    onPresetConfigChange,
    disabled,
}: CloverOrganizationIpgConfigEditorProps): ReactElement {
    const environmentAvailabilityQuery = useQuery({
        queryKey: [
            'sambaConfig',
            'cloverRuntimeEnvironmentAvailability',
            organizationUuid,
        ],
        queryFn: () =>
            sambaConfigApi.getCloverRuntimeEnvironmentAvailability(
                organizationUuid
            ),
        retry: false,
    });

    return (
        <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm font-medium">Clover gateway configuration</p>
            <div className="space-y-2">
                <p className="text-sm font-medium">Environment</p>
                <div className="flex flex-wrap gap-4">
                    {([
                        CloverGatewayEnvironment.PRODUCTION,
                        CloverGatewayEnvironment.SANDBOX,
                    ] as const).map((environment) => {
                        const isAvailable =
                            environmentAvailabilityQuery.data?.environments[
                                environment
                            ] === true;
                        const isDisabled = disabled || !isAvailable;
                        return (
                            <CloverOrganizationIpgEnvironmentRadio
                                key={environment}
                                label={formatCloverOrganizationIpgEnvironmentLabel(
                                    environment
                                )}
                                checked={editorState.environment === environment}
                                disabled={isDisabled}
                                tooltip={getCloverEnvironmentRadioTooltip({
                                    environment,
                                    isPending:
                                        environmentAvailabilityQuery.isPending,
                                    isError:
                                        environmentAvailabilityQuery.isError,
                                    isAvailable,
                                    editorDisabled: disabled,
                                })}
                                onChange={() => {
                                    const nextState =
                                        buildCloverOrganizationIpgPresetState(
                                            editorState,
                                            environment
                                        );
                                    onChange(nextState);
                                    onPresetConfigChange(
                                        buildCloverOrganizationIpgConfigForSaveOrThrow(
                                            nextState
                                        )
                                    );
                                }}
                            />
                        );
                    })}
                </div>
            </div>
            <p className="text-sm">Portal URL: {editorState.portalUrl}</p>
        </div>
    );
}

function getCloverTerminalDeviceDisplayLabel(
    terminalDevice: TerminalDeviceListItem
): string {
    const configuredName = terminalDevice.name?.trim();
    if (configuredName !== undefined && configuredName.length > 0) {
        return configuredName;
    }
    return [
        terminalDevice.processor_name,
        terminalDevice.processor_mid,
        terminalDevice.processor_tid,
        terminalDevice.processor_device_id,
    ].join(' / ');
}

function CloverReadinessCheckSummary(props: {
    readonly check: CloverReadinessCheck;
    readonly label: string;
}): ReactElement {
    const statusLabel: string = (() => {
        switch (props.check.status) {
            case CloverReadinessCheckStatus.READY:
                return 'Ready';
            case CloverReadinessCheckStatus.UNVERIFIED:
                return 'Not verified';
            case CloverReadinessCheckStatus.NOT_READY:
                return 'Needs attention';
        }
    })();
    return (
        <div className="rounded border p-2" data-readiness-check={props.label}>
            <p className="text-sm font-medium">{props.label}</p>
            <p className="text-sm">{statusLabel}</p>
            {props.check.status !== CloverReadinessCheckStatus.READY && (
                <p className="text-sm text-muted-foreground">
                    {props.check.message}
                </p>
            )}
        </div>
    );
}

export function CloverOrganizationIpgReadinessSummary({
    organizationUuid,
    portalUrl,
}: CloverOrganizationIpgReadinessSummaryProps): ReactElement {
    const queryClient = useQueryClient();
    const [selectedTerminalDevice, setSelectedTerminalDevice] =
        useState<TerminalDeviceListItem | null>(null);
    const selectedTerminalDeviceUuid =
        selectedTerminalDevice?.terminal_device_uuid ?? null;
    const readinessQuery = useQuery<CloverReadinessResult>({
        queryKey: [
            'paymentsInfrastructure',
            'cloverReadiness',
            organizationUuid,
            selectedTerminalDeviceUuid ?? 'none',
        ],
        queryFn: async () => {
            if (selectedTerminalDeviceUuid === null) {
                throw new Error(
                    'A terminal device must be selected for Clover readiness.'
                );
            }
            return await paymentsInfrastructureApi.getCloverReadiness(
                organizationUuid,
                selectedTerminalDeviceUuid
            );
        },
        enabled: selectedTerminalDeviceUuid !== null,
        retry: false,
    });
    const oauthStatusQuery = useQuery<CloverOAuthAuthorizationStatus>({
        queryKey: ['paymentsInfrastructure', 'cloverOAuthStatus', organizationUuid],
        queryFn: async () =>
            await paymentsInfrastructureApi.getCloverOAuthAuthorizationStatus(
                organizationUuid
            ),
        retry: false,
    });
    const invalidateMutation = useMutation<CloverOAuthAuthorizationStatus, Error>({
        mutationFn: async () =>
            await paymentsInfrastructureApi.invalidateCloverOAuthAuthorization(
                organizationUuid
            ),
        onSuccess: (status) => {
            queryClient.setQueryData(
                ['paymentsInfrastructure', 'cloverOAuthStatus', organizationUuid],
                status
            );
            readinessQuery.refetch().catch(() => undefined);
        },
    });
    const authorizationPath =
        selectedTerminalDeviceUuid === null
            ? null
            : `/api/clover/oauth/authorize/terminal/launch?${new URLSearchParams({
                  organization_uuid: organizationUuid,
                  terminal_device_uuid: selectedTerminalDeviceUuid,
              }).toString()}`;
    const reauthorizationPath = `/api/clover/oauth/reauthorize/launch?${new URLSearchParams({
        organization_uuid: organizationUuid,
    }).toString()}`;

    return (
        <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm font-medium">Clover readiness</p>
            {oauthStatusQuery.isPending ? (
                <p className="text-sm" role="status">Loading Clover authorization lease...</p>
            ) : oauthStatusQuery.isError ? (
                <Alert variant="destructive">
                    <AlertDescription>Unable to load Clover authorization lease.</AlertDescription>
                </Alert>
            ) : (
                <div className="space-y-1 rounded border p-2">
                    <p className="text-sm font-medium">OAuth authorization</p>
                    <p className="text-sm">Status: {oauthStatusQuery.data.state}</p>
                    {oauthStatusQuery.data.state === CloverOAuthAuthorizationState.ACTIVE ? (
                        <>
                            <p className="text-sm">
                                Access token expires: {formatOAuthExpiration(
                                    oauthStatusQuery.data.access_token_expires_ts
                                )}
                            </p>
                            <p className="text-sm">
                                Authorization lease expires: {formatOAuthExpiration(
                                    oauthStatusQuery.data.refresh_token_expires_ts
                                )}
                            </p>
                            <p className="text-sm text-muted-foreground">
                                Anchorbase renews the access token automatically when Clover access
                                is needed.
                            </p>
                            <Button variant="outline" asChild>
                                <a href={reauthorizationPath} target="_blank" rel="noreferrer">
                                    Renew Clover authorization
                                </a>
                            </Button>
                            <Button
                                type="button"
                                variant="destructive"
                                disabled={invalidateMutation.isPending}
                                onClick={() => {
                                    if (
                                        window.confirm(
                                            'Invalidate Clover authorization for this organization? All Clover payments will stop until authorization succeeds again.'
                                        )
                                    ) {
                                        invalidateMutation.mutate();
                                    }
                                }}
                            >
                                {invalidateMutation.isPending
                                    ? 'Invalidating Clover authorization...'
                                    : 'Invalidate Clover authorization'}
                            </Button>
                        </>
                    ) : oauthStatusQuery.data.state ===
                      CloverOAuthAuthorizationState.REAUTHORIZATION_REQUIRED ? (
                        <>
                            <p className="text-sm text-muted-foreground">
                                The stored Clover authorization is not usable. Reauthorize Clover
                                to issue a new token lease.
                            </p>
                            <div className="flex flex-wrap gap-2">
                                <Button variant="outline" asChild>
                                    <a href={portalUrl} target="_blank" rel="noreferrer">
                                        Log into Clover
                                    </a>
                                </Button>
                                <Button variant="outline" asChild>
                                    <a
                                        href={reauthorizationPath}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Reauthorize Clover
                                    </a>
                                </Button>
                            </div>
                        </>
                    ) : (
                        <p className="text-sm text-muted-foreground">
                            No Clover authorization is connected.
                        </p>
                    )}
                    {invalidateMutation.isError && (
                        <p className="text-sm text-destructive" role="alert">
                            Unable to invalidate Clover authorization.
                        </p>
                    )}
                </div>
            )}
            <OmnisearchSelect<TerminalDeviceListItem, string>
                label="Terminal device"
                placeholder="Search terminal devices..."
                pageSize={CLOVER_READINESS_TERMINAL_PAGE_SIZE}
                resetToken={organizationUuid}
                overlayMenu={true}
                selectedItemId={selectedTerminalDeviceUuid}
                selectedItemLabel={
                    selectedTerminalDevice === null
                        ? undefined
                        : getCloverTerminalDeviceDisplayLabel(
                              selectedTerminalDevice
                          )
                }
                fetchPage={async (params) => {
                    const response =
                        await paymentsInfrastructureApi.searchTerminalDevices({
                            organization_uuid: organizationUuid,
                            include_descendants: false,
                            ...(params.terms !== undefined
                                ? { terms: [...params.terms] }
                                : {}),
                            limit: Math.min(
                                params.limit,
                                CLOVER_READINESS_TERMINAL_PAGE_SIZE
                            ),
                            cursor: params.cursor,
                        });
                    return {
                        items: response.terminal_devices,
                        pagination: response.pagination,
                    };
                }}
                getItemId={(terminalDevice) =>
                    terminalDevice.terminal_device_uuid
                }
                getItemLabel={getCloverTerminalDeviceDisplayLabel}
                getItemFilterText={(terminalDevice) =>
                    [
                        getCloverTerminalDeviceDisplayLabel(terminalDevice),
                        terminalDevice.terminal_device_uuid,
                    ]
                        .join(' ')
                        .toLowerCase()
                }
                onSelect={setSelectedTerminalDevice}
                onClearSelection={() => setSelectedTerminalDevice(null)}
            />

            {selectedTerminalDeviceUuid === null ? (
                <p className="text-sm text-muted-foreground">
                    Select a terminal device to check Clover readiness.
                </p>
            ) : readinessQuery.isPending ? (
                <p className="text-sm" role="status">
                    Checking Clover readiness...
                </p>
            ) : readinessQuery.isError ? (
                <Alert variant="destructive">
                    <AlertDescription>
                        <p>
                            {readinessQuery.error instanceof ApiError &&
                            readinessQuery.error.status === 400
                                ? readinessQuery.error.message
                                : 'Unable to check Clover readiness.'}
                        </p>
                        <Button
                            type="button"
                            variant="outline"
                            className="mt-2"
                            onClick={() => void readinessQuery.refetch()}
                        >
                            Retry readiness check
                        </Button>
                        {readinessQuery.error instanceof ApiError &&
                            readinessQuery.error.status === 400 &&
                            authorizationPath !== null &&
                            readinessQuery.error.message.startsWith(
                                'Clover OAuth authorization '
                            ) && (
                                <div className="mt-2 space-y-2">
                                    <p className="text-sm">
                                        Log into Clover first, then authorize
                                        Anchorbase.
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        <Button variant="outline" asChild>
                                            <a
                                                href={portalUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                Log into Clover
                                            </a>
                                        </Button>
                                        <Button variant="outline" asChild>
                                            <a
                                                href={authorizationPath}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                Authorize Clover
                                            </a>
                                        </Button>
                                    </div>
                                </div>
                            )}
                    </AlertDescription>
                </Alert>
            ) : (
                <div className="space-y-3">
                    {readinessQuery.isFetching && (
                        <p className="text-sm" role="status">
                            Refreshing Clover readiness...
                        </p>
                    )}
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                        {CloverReadinessCheckDefinitions.map(
                            (definition) => (
                                <CloverReadinessCheckSummary
                                    key={definition.key}
                                    label={definition.label}
                                    check={
                                        readinessQuery.data[definition.key]
                                    }
                                />
                            )
                        )}
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        disabled={readinessQuery.isFetching}
                        onClick={() => void readinessQuery.refetch()}
                    >
                        Retry readiness check
                    </Button>
                </div>
            )}
        </div>
    );
}

export function CloverOrganizationIpgReadOnlySummary({
    organizationUuid,
    editorState,
}: CloverOrganizationIpgReadOnlySummaryProps): ReactElement {
    return (
        <div className="space-y-2">
            <p className="text-sm font-medium">Clover gateway configuration</p>
            <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                <p>
                    Environment:{' '}
                    {formatCloverOrganizationIpgEnvironmentLabel(
                        editorState.environment
                    )}
                </p>
                <p>Portal URL: {editorState.portalUrl}</p>
                {editorState.capabilityDisplayRules
                    .filter(
                        (rule) =>
                            !CloverDynamicReadinessFeatureIds.has(
                                rule.featureId
                            )
                    )
                    .map((rule) => {
                        const isSupported =
                            rule.state ===
                            IpgPaymentFeatureCapabilityState.SUPPORTED;
                        return (
                            <p key={rule.featureId}>
                                {rule.label}:{' '}
                                <span
                                    title={rule.tooltip}
                                    aria-disabled={
                                        isSupported ? undefined : true
                                    }
                                    className={
                                        isSupported
                                            ? undefined
                                            : 'opacity-50'
                                    }
                                >
                                    {formatIpgPaymentFeatureCapabilityStateLabel(
                                        rule.state
                                    )}
                                </span>
                            </p>
                        );
                    })}
            </div>
            <CloverOrganizationIpgReadinessSummary
                organizationUuid={organizationUuid}
                portalUrl={editorState.portalUrl}
            />
        </div>
    );
}

export const CloverOrganizationIpgConfigEditorAdapter: IpgConfigEditorAdapter<
    CloverOrganizationIpgConfigEditorState,
    CloverIpgConfigCustomization
> = {
    ipgSystemId: CloverIpgSystemId,
    ipgSystemIdKey: CloverIpgSystemIdKey,
    label: 'Clover',
    capabilities: {
        supportsEditableConfig: true,
        supportsReadOnlySummary: true,
        supportsDefaultCredentialPlaceholders: true,
    },
    createEditableState: ({ merchantConfig }) =>
        buildCloverOrganizationIpgConfigEditorState(merchantConfig),
    validateEditableState: ({ editorState }) => {
        buildCloverOrganizationIpgConfigForSaveOrThrow(editorState);
        return [];
    },
    buildSavePayload: ({ editorState }) =>
        buildCloverOrganizationIpgConfigForSaveOrThrow(editorState),
    renderEditable: ({ organizationUuid, editorState, onChange, disabled }) => (
        <CloverOrganizationIpgConfigEditor
            organizationUuid={organizationUuid}
            editorState={editorState}
            onChange={onChange}
            onPresetConfigChange={() => undefined}
            disabled={disabled}
        />
    ),
    renderReadOnlySummary: ({ merchantConfig, organizationUuid }) => (
        <CloverOrganizationIpgReadOnlySummary
            organizationUuid={organizationUuid}
            editorState={buildCloverOrganizationIpgConfigEditorState(
                merchantConfig
            )}
        />
    ),
};
